import { parsePhoneNumber } from 'libphonenumber-js';
import moment from 'moment-timezone';

import { formattedAddress } from '@/helpers/address';
import { capitalizeFirstLetter } from '@/helpers/string';
import { normalizeJobExperience } from '@/helpers/workerJobExperience';
import i18n from '@/i18n';
import { useConfigurationStore } from '@/store/configurationStore';

import type { IAddress } from '@/types/address';
import type { IWorkerJob } from '@/types/job';

interface AskedChanges {
	birthPlace?: string;
	address?: IAddress & { department: string; region: string };
	firstname?: string;
	phone?: string;
	gender?: string;
	birthDate?: number;
	lastname?: string;
	jobs?: IWorkerJob[];
}

interface Item {
	field: string;
	oldValue: string | null;
	newValue: string | null;
}

export const useAskChangesFormater = () => {
	const configStore = useConfigurationStore();
	const jobOptions = configStore.jobOptions;
	const degreeOptions: { label: string; value: string }[] =
		configStore.degreeLevels.map((degreeLevel) => {
			return { label: degreeLevel.name, value: degreeLevel.slug };
		});
	const formatJob = (jobToFormat?: IWorkerJob): string => {
		if (!jobToFormat) return '';
		const jobName = jobOptions.find(
			(job) => job.value === jobToFormat.jobId
		)?.label;
		const degreeLevel = degreeOptions.find(
			(degree) => degree.value === jobToFormat.degreeLevel || ''
		)?.label;
		const experience = normalizeJobExperience(jobToFormat.experience);

		return [
			jobName,
			degreeLevel,
			experience ? i18n.global.t(`jobExperienceShort.${experience}`) : '',
		]
			.filter(Boolean)
			.join(' - ');
	};

	// biome-ignore lint/suspicious/noExplicitAny: Init Biome
	const askChangesFormater = (worker: any) => {
		const askChangesFormated: Item[] = [];
		if (!worker?.askedChanges) {
			return askChangesFormated;
		}

		for (const [key, value] of Object.entries(
			worker.askedChanges as AskedChanges
		)) {
			switch (key) {
				case 'gender':
					break;

				case 'address': {
					const oldAddress = worker.address;

					askChangesFormated.push({
						field: key,
						oldValue: formattedAddress(oldAddress as IAddress),
						newValue: formattedAddress(value as IAddress),
					});
					break;
				}

				case 'birthDate':
					askChangesFormated.push({
						field: key,
						oldValue: worker.birthDate,
						newValue: moment(value).format('YYYY-MM-DD'),
					});
					break;

				case 'phone':
					askChangesFormated.push({
						field: key,
						oldValue: worker[key],
						newValue: parsePhoneNumber(value, 'FR').formatNational(),
					});
					break;

				case 'jobs': {
					const newJobs = (value as IWorkerJob[]).filter(
						(elem) =>
							!(worker.jobs as IWorkerJob[]).some(
								(job) => job.jobId === elem.jobId
							)
					);
					const deleteJobs = (worker.jobs as IWorkerJob[]).filter(
						// biome-ignore lint/suspicious/noExplicitAny: Init Biome
						(elem) => !value.some((job: any) => job.jobId === elem.jobId)
					);
					const updatedJobs = (value as IWorkerJob[]).filter((elem) =>
						(worker.jobs as IWorkerJob[]).some(
							(job) => job.jobId === elem.jobId
						)
					);
					newJobs.forEach((job) => {
						askChangesFormated.push({
							field: key,
							oldValue: null,
							newValue: formatJob(job),
						});
					});
					deleteJobs.forEach((job) => {
						askChangesFormated.push({
							field: key,
							oldValue: formatJob(job),
							newValue: null,
						});
					});
					updatedJobs.forEach((job) => {
						const newValue = formatJob(job);
						const oldValue = formatJob(
							(worker.jobs as IWorkerJob[]).find(
								(wjob) => wjob.jobId === job.jobId
							)
						);

						if (newValue !== oldValue) {
							askChangesFormated.push({
								field: key,
								oldValue,
								newValue,
							});
						}
					});
					break;
				}

				case 'lastname':
				case 'firstname':
					askChangesFormated.push({
						field: key,
						oldValue: capitalizeFirstLetter(worker[key]),
						newValue: capitalizeFirstLetter(value.toString()),
					});
					break;

				default:
					askChangesFormated.push({
						field: key,
						oldValue: worker[key],
						newValue: value.toString(),
					});
					break;
			}
		}

		return askChangesFormated;
	};

	return { askChangesFormater, formatJob };
};
